import { IconsClose } from '@doltech/icons/IconsClose';
import { IconSystemV2InterfaceEssentialMessageQuestion } from '@doltech/icons/SystemV2/IconSystemV2InterfaceEssentialMessageQuestion';
import { compose } from '@doltech/utils/lib/compose';

import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { IconCourseCheck } from '@doltech/icons/IconCourseCheck';
import { ButtonSizableContent } from '../../Button/ButtonGeneral/ButtonSizableContent';
import { ButtonStyles } from '../../Button/ButtonStyles/ButtonStyles';
import { colorsV2 } from '../../colors-v2';
import { Typography } from '../../DesignSystemV2/Typography';
import { ImageInfo } from '../../FileUpload/AdminFileUpload';
import { InputContentGeneral } from '../../Input/InputContentGeneral/InputContentGeneral';
import { InputLightStyleLayout } from '../../Input/InputLightStyleLayout/InputLightStyleLayout';
import { ResponsiveTypography } from '../../Typography/v3/ResponsiveTypography';
import { useRiddleCardStore, withRiddleCardStore } from './riddle-card.store';

interface RiddleItem {
  question: string;
  correctAnswer: string;
  image: ImageInfo;
}

interface RiddleCardProps {
  data: RiddleItem;
  isLogged?: boolean;
}

const Main = styled.div`
  border: 1px solid ${colorsV2.primary60};
  background-color: ${colorsV2.primary5};
  border-radius: 8px;
  padding: 44px 24px 32px;

  position: relative;
  .title {
    position: absolute;
    top: -20px;
    left: 0;
    right: 0;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
    width: fit-content;
    background-color: ${colorsV2.white100};

    padding: 10px 12px;
    border-radius: 20px;
    border: 1px solid ${colorsV2.primary60};
  }
`;

const NotSubmitted = styled.div`
  text-align: center;
  display: grid;
  gap: 28px;
  justify-content: center;
  .actions-btn {
    width: fit-content;
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: max-content;
    gap: 12px;
    margin: 20px auto 0;
  }
`;
const AnsweredWrapper = styled.div`
  .content {
    display: grid;
    grid-auto-columns: minmax(0, 1fr);
    place-items: center;
    gap: 16px;

    text-align: center;
    margin-bottom: 28px;
  }
  .image-wrapper {
    width: 124px;
    border-radius: 8px;
    overflow: hidden;
    margin: 0 auto;

    img {
      width: 100%;
      object-fit: cover;
    }
  }
`;

const RiddleLabelWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const RiddleCardBase = () => {
  const store = useRiddleCardStore();

  const incorrectRiddleLabel = React.useMemo(() => {
    if (store.statistics?.noOfIncorrectSelections > 0) {
      return `DON'T BE SAD ! ${store.statistics?.noOfIncorrectSelections} USERS CAN’T SOLVE IT`;
    }
    return "DON'T BE SAD ! THERE ARE MANY USERS CAN’T SOLVE IT";
  }, [store.statistics?.noOfIncorrectSelections]);

  const renderContent = React.useCallback(() => {
    if (!store.isSubmitted) {
      return (
        <NotSubmitted>
          <div className="icon-wrapper">
            <IconSystemV2InterfaceEssentialMessageQuestion color="primary100" size={36} />
          </div>
          <Typography.Paragraph variant="semi-bold/20" color="black100">
            {store.question}
          </Typography.Paragraph>
          <div className="action-wrapper">
            <InputLightStyleLayout
              className="answer-input"
              size="large"
              inputContent={
                <InputContentGeneral
                  value={store.userAnswer}
                  onChange={(e) => store.setUserAnswer(e)}
                  placeholder="Điền câu trả lời..."
                />
              }
            />
            <div className="actions-btn">
              <ButtonStyles
                buttonStyle="outline"
                color="dark"
                colorStyle="matte"
                content={<ButtonSizableContent text="Không biết" />}
                onClick={() => store.userDontKnow()}
              />
              <ButtonStyles
                buttonStyle="filled"
                color="primary"
                colorStyle="bold"
                content={<ButtonSizableContent text="Kiểm tra" />}
                onClick={() => store.checkUserAnswer()}
                disabled={!store.userAnswer || store.userAnswer.trim().length === 0}
              />
            </div>
          </div>
        </NotSubmitted>
      );
    }
    return (
      <AnsweredWrapper>
        <div className="content">
          <RiddleLabelWrapper>
            {store.isUserAnswerCorrect ? (
              <>
                <IconCourseCheck color="green100" size={18} />
                <Typography.Paragraph color="green100" variant="semi-bold/14-20" ml="4px">
                  YOU ARE A GREAT RIDDLE SOLVER !
                </Typography.Paragraph>
              </>
            ) : (
              <>
                <IconsClose color="primary100" size={18} />
                <Typography.Paragraph color="primary100" variant="semi-bold/14-20" ml="4px">
                  {incorrectRiddleLabel}
                </Typography.Paragraph>
              </>
            )}
          </RiddleLabelWrapper>

          <Typography.Paragraph variant="semi-bold/20" color="black100">
            {store.question}
          </Typography.Paragraph>
          <Typography.Paragraph color="green100" variant="semi-bold/20">
            {store.correctAnswer}
          </Typography.Paragraph>
        </div>

        {store.image?.url && (
          <div className="image-wrapper">
            <img src={store.image.url} alt="riddle" />
          </div>
        )}
      </AnsweredWrapper>
    );
  }, [store, incorrectRiddleLabel]);

  return (
    <Main>
      <div className="title">
        <ResponsiveTypography.Title as="h2" variant="bold/14-20" color="primary100">
          CÂU ĐỐ HÔM NAY
        </ResponsiveTypography.Title>
      </div>
      {renderContent()}
    </Main>
  );
};

export const RiddleCard: React.FC<RiddleCardProps> = compose(
  withRiddleCardStore,
  observer
)(RiddleCardBase);
